/**
 * Main Redux Store configuration.
 *
 * Some useful links:
 * Redux         @tutorial https://github.com/reduxjs/redux
 * Redux Thunk   @tutorial https://github.com/reduxjs/redux-thunk
 * Redux Persist @tutorial https://github.com/rt2zz/redux-persist
 */

import {
    createStore,
    applyMiddleware,
} from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';
import freeze from 'redux-freeze';

/**
 * Packages used to connect React Router to Redux.
 * More info:
 * Connected React Router @tutorial https://github.com/supasate/connected-react-router
 */
import { createBrowserHistory } from 'history';
import { routerMiddleware } from 'connected-react-router';

/**
 * Reducers
 */
import reducers from './reducers';

/**
 * @function configStore
 * @param {?object} initialState Optional initial state to store.
 *
 * @description Configures redux store with redux persist,
 * and adds HotModuleReolad support.
 */
const configStore = (initialState, api) => {
    /**
   * @description Browser history.
   */
    const history = createBrowserHistory();

    /**
     * @description Redux middlewares.
     */
    let middlewares = [
        routerMiddleware(history),
        thunk.withExtraArgument(api),
    ];

    if (process.env.NODE_ENV === 'development') {
        middlewares = [
            ...middlewares,
            freeze,
        ]
    }

    /**
    * @description Redux store.
    */
    const reduxStore = createStore(
        reducers(history),
        initialState,
        applyMiddleware(
            ...middlewares,
        ),
    );

    /**
   * @description Store persistor.
   */
    const persistor = persistStore(reduxStore);

    if (module.hot) {
        /**
         * Enable Webpack hot module replacement for reducers.
         */
        module.hot.accept(() => {
            /**
             * We need to require for hot reloading to work properly.
             */
            /* eslint global-require: 0 */
            const nextRootReducer = (require('./reducers').default)(history);
            reduxStore.replaceReducer(
                persistReducer({
                    key: 'root',
                    storage,
                    whitelist: [],
                }, nextRootReducer),
            );
        });
    }

    return { reduxStore, persistor, history };
};

export default configStore;
